import React from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, ChevronRight } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, StatusBadge } from "@/components/common";

function flattenTree(node, depth, rows) {
  rows.push({ key: node.id, type: node.type, name: node.name, depth, asset: false });
  const children = node.sites || node.areas || node.units || null;
  if (children) children.forEach((c) => flattenTree(c, depth + 1, rows));
  if (node.assets) node.assets.forEach((a) => rows.push({ key: node.id + a, type: "Asset", name: a, depth: depth + 1, asset: true }));
  return rows;
}

export default function TwinExplorer() {
  const navigate = useNavigate();
  const { data: twins } = usePoll(endpoints.twins, 5000);
  const { data: onto } = usePoll(endpoints.ontology, 60000);
  if (!twins || !onto) return <Loading />;

  const rows = flattenTree(onto.tree, 0, []);

  return (
    <div data-testid="twin-explorer-page">
      <PageHeader icon={Boxes} title="Twin Explorer"
        subtitle="The engineering view — every digital twin, its health, and the enterprise ontology it derives from." />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel testid="panel-ontology" title="Enterprise Ontology" subtitle="Enterprise → Site → Area → Unit → Asset">
          <div className="space-y-0.5">
            {rows.map((r) => (
              <div key={r.key} style={{ marginLeft: r.depth * 14 }}
                className={`flex items-center gap-1.5 py-0.5 ${r.asset ? "text-[12px] text-slate-500" : "text-sm"}`}>
                <span className={`text-[10px] font-bold uppercase tracking-wide w-[70px] shrink-0 ${r.asset ? "text-slate-300" : "text-slate-400"}`}>{r.type}</span>
                <span className={r.asset ? "" : "text-slate-700 font-medium"}>{r.name}</span>
              </div>
            ))}
          </div>
        </Panel>

        <div className="xl:col-span-2">
          <Panel testid="panel-twin-registry" title="Digital Twin Registry" subtitle="7 core twins · click to open a workspace">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {twins.twins.map((t) => {
                const openable = ["refrigeration", "boiler", "pasteurization"].includes(t.id);
                return (
                  <button key={t.id} onClick={() => navigate(`/twin/${t.id}`)} data-testid={`twin-card-${t.id}`}
                    className="text-left border border-slate-200 rounded-md p-3 hover:border-slate-300 hover:shadow-sm transition-all">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-slate-900">{t.name}</span>
                      <StatusBadge status={t.health} />
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">{t.category} · {t.libraries.join(", ")}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono text-slate-600">
                      {Object.entries(t.metrics).slice(0, 3).map(([k, v]) => (
                        <span key={k}>{k.replace(/_/g, " ")}: <b>{typeof v === "number" ? v : String(v)}</b></span>
                      ))}
                    </div>
                    {openable && <div className="mt-2 text-[11px] text-blue-600 flex items-center gap-1">Open workspace <ChevronRight size={12} /></div>}
                  </button>
                );
              })}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
